import React, { useMemo, useState, useCallback } from 'react';
import EvidenceCard from '../../components/EvidenceCard';
import ComplianceAlertCard from '../../components/ComplianceAlertCard';
import WaybackTimelineCard from '../../components/WaybackTimelineCard';
import CategoryTabs from '../../components/investigation/CategoryTabs';
import { classifyMentionCategory } from '../../utils/mentionCategory';
import { stripHtmlTags } from '../../utils/personFeedDisplay';

/** Horizontal Person Search feed filters (replaces rigid Work/Social/Security pagination). */
export const PERSON_FEED_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'profiles', label: 'Profiles' },
  { id: 'business', label: 'Business' },
  { id: 'mentions', label: 'Mentions' },
  { id: 'potential', label: 'Potential matches' },
];

const FILTER_IDS = new Set(PERSON_FEED_FILTERS.map((f) => f.id));

const BUSINESS_HOST_HINTS = [
  // Multi-jurisdiction firm / registry / clinic indexes
  'firme.ro',
  'listafirme.ro',
  'termene.ro',
  'risco.ro',
  'ghidulprimariilor.ro',
  'quickconta.ro',
  'totalfirme.ro',
  'northdata.com',
  'medatlas.ro',
  'tenderinfo.org',
  'opencorporates.com',
  'companieshouse.gov.uk',
  'creditinfo.ro',
  'datefirme.ro',
  'romanian-companies.eu',
  'bloomberg.com',
  'dnb.com',
  'creditsafe.com',
  'handelsregister.de',
  'infogreffe.fr',
  'kvk.nl',
];

const BUSINESS_TEXT_RE =
  /\bpfa\b|\bs\.?\s*r\.?\s*l\.?\b|\bsrl\b|\bltd\.?\b|\bgmbh\b|\bllc\b|\bplc\b|\binc\.?\b|întreprindere\s+individual|intreprindere\s+individual|persoan[ăa]\s+fizic|\bcabinet\b|\bclinic[ăae]\b|\bcui\b|\bro\s*j\d{6,}|companies\s+house|opencorporates/i;

/**
 * Category filter for Person Discovery feed.
 */
export function usePersonCategoryFilter() {
  const [activeFilter, setActiveFilter] = useState('all');

  const toggleCategory = useCallback((labelOrId) => {
    const id = String(labelOrId || '').trim();
    if (!FILTER_IDS.has(id)) return;
    setActiveFilter((prev) => (prev === id ? 'all' : id));
  }, []);

  const setFilter = useCallback((id) => {
    if (!FILTER_IDS.has(id)) return;
    setActiveFilter(id);
  }, []);

  const resetFilter = useCallback(() => setActiveFilter('all'), []);

  return { activeFilter, toggleCategory, setFilter, resetFilter };
}

function hostFromUrl(url) {
  try {
    const h = new URL(String(url || '')).hostname.toLowerCase();
    return h.startsWith('www.') ? h.slice(4) : h;
  } catch {
    return '';
  }
}

/**
 * Classify a Person Search row for the feed filter bar.
 *
 * @param {object} r
 * @returns {'profiles'|'business'|'mentions'|'potential'|'meta'|null}
 */
export function classifyPersonFeedCategory(r) {
  if (!r) return null;
  if (r.is_hidden) return 'potential';
  if (r.ui_bucket === 'compliance_alert' || r.ui_bucket === 'wayback_archive') return 'meta';

  const dem = String(r.secondary_demotion_reason || '');
  const sourceType = String(r.source_type || '');
  const host = hostFromUrl(r.url);
  const hay = `${r.title || ''} ${r.snippet || ''} ${r.url || ''}`.toLowerCase();

  const isBusiness =
    dem === 'registry_pfa' ||
    dem === 'name_variation' ||
    sourceType === 'Business Index' ||
    sourceType === 'Legal Record' ||
    BUSINESS_HOST_HINTS.some((h) => host === h || host.endsWith(`.${h}`)) ||
    BUSINESS_TEXT_RE.test(hay);

  if (isBusiness) return 'business';
  if (isPublicMentionRow(r)) return 'mentions';
  if (isVerifiedIntelligenceRow(r) || r.is_primary_identity === true) return 'profiles';
  if (r.validation_status === 'passed') return 'mentions';
  return null;
}

export function filterResultsByCategory(results, activeFilter) {
  if (!activeFilter || activeFilter === 'all') return results;
  return (results || []).filter((r) => {
    const cat = classifyPersonFeedCategory(r);
    if (activeFilter === 'profiles') {
      return cat === 'profiles' || cat === 'meta' || r.is_primary_identity === true;
    }
    if (activeFilter === 'business') return cat === 'business';
    if (activeFilter === 'mentions') return cat === 'mentions';
    if (activeFilter === 'potential') return cat === 'potential' || r.is_hidden === true;
    return true;
  });
}

/** Primary identity first, then score DESC (LinkedIn / press before PFA registry). */
export function sortPersonIntelRows(rows) {
  return [...(rows || [])].sort((a, b) => {
    const pa = a?.is_primary_identity === true ? 1 : 0;
    const pb = b?.is_primary_identity === true ? 1 : 0;
    if (pb !== pa) return pb - pa;
    return (Number(b?.score) || 0) - (Number(a?.score) || 0);
  });
}

export function isVerifiedIntelligenceRow(r) {
  if (!r || r.ui_bucket === 'compliance_alert' || r.ui_bucket === 'wayback_archive' || r.is_hidden) {
    return false;
  }
  return r.is_mention === false && r.validation_status === 'passed';
}

/** Strict Mentions bucket. */
export function isPublicMentionRow(r) {
  if (!r || r.ui_bucket === 'compliance_alert' || r.ui_bucket === 'wayback_archive' || r.is_hidden) {
    return false;
  }
  return r.is_mention === true;
}

const extractContextSnippet = (res) => {
  const raw = stripHtmlTags(`${res.title || ''} ${res.snippet || ''}`);
  if (!raw) return '';
  return raw.length > 420 ? `${raw.slice(0, 420)}…` : raw;
};

function PersonFeedFilterBar({ activeFilter, onFilterChange, counts }) {
  return (
    <CategoryTabs
      filters={PERSON_FEED_FILTERS}
      activeFilter={activeFilter}
      onFilterChange={onFilterChange}
      counts={counts}
      ariaLabel="Person result categories"
      testId="person-feed-filters"
      hideZeroCounts
      mobileEssentialIds={['all', 'profiles', 'mentions']}
    />
  );
}

const PersonDiscovery = ({
  displayResults,
  loading,
  getDisplayName,
  activeFilter = 'all',
  onFilterChange,
  extractedLeads = null,
}) => {
  const [noiseOpen, setNoiseOpen] = useState(false);
  const [mentionsOpen, setMentionsOpen] = useState(true);

  const globalContacts = useMemo(() => {
    const emails = Array.isArray(extractedLeads?.emails)
      ? extractedLeads.emails.map((e) => String(e || '').trim()).filter(Boolean)
      : [];
    const phones = Array.isArray(extractedLeads?.phones)
      ? extractedLeads.phones.map((p) => String(p || '').trim()).filter(Boolean)
      : [];
    return { emails, phones };
  }, [extractedLeads]);

  const filterCounts = useMemo(() => {
    const counts = { all: 0, profiles: 0, business: 0, mentions: 0, potential: 0 };
    (displayResults || []).forEach((r) => {
      if (r.is_hidden) {
        counts.potential += 1;
        return;
      }
      counts.all += 1;
      const cat = classifyPersonFeedCategory(r);
      if (cat === 'profiles' || cat === 'meta') counts.profiles += 1;
      if (cat === 'business') counts.business += 1;
      if (cat === 'mentions') counts.mentions += 1;
    });
    return counts;
  }, [displayResults]);

  const filteredResults = useMemo(
    () => filterResultsByCategory(displayResults, activeFilter),
    [displayResults, activeFilter],
  );

  const { complianceAlerts, waybackArchives, verifiedResults, mentionResults, hiddenResults } =
    useMemo(() => {
      const compliance = [];
      const wayback = [];
      const verified = [];
      const mentions = [];
      const hidden = [];
      filteredResults.forEach((r) => {
        if (r.ui_bucket === 'compliance_alert') {
          compliance.push(r);
          return;
        }
        if (r.ui_bucket === 'wayback_archive') {
          wayback.push(r);
          return;
        }
        if (r.is_hidden) {
          hidden.push(r);
          return;
        }
        if (isVerifiedIntelligenceRow(r)) {
          verified.push(r);
          return;
        }
        if (isPublicMentionRow(r)) {
          mentions.push(r);
          return;
        }
        if (r.validation_status === 'passed') {
          mentions.push(r);
        } else {
          hidden.push(r);
        }
      });
      return {
        complianceAlerts: compliance,
        waybackArchives: wayback,
        verifiedResults: sortPersonIntelRows(verified),
        mentionResults: sortPersonIntelRows(mentions),
        hiddenResults: sortPersonIntelRows(hidden),
      };
    }, [filteredResults]);

  const primaryTargetKey = useMemo(() => {
    if (!verifiedResults.length) return null;
    const primaries = verifiedResults.filter((r) => r.is_primary_identity === true);
    const pool = primaries.length ? primaries : verifiedResults;
    const top = sortPersonIntelRows(pool)[0];
    return top?.url || top?.ui_bucket || null;
  }, [verifiedResults]);

  if ((displayResults || []).length === 0 && loading) {
    return (
      <div className="flex h-64 flex-col items-center justify-center space-y-4 text-slate-300">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-cyan-500 border-t-transparent" />
        <p className="text-sm font-medium italic">Deploying deep search protocols…</p>
      </div>
    );
  }

  const renderCard = (res, idx, { isMentionCard = false } = {}) => {
    const name = getDisplayName(res);
    if (!name) return null;
    const snippet = extractContextSnippet(res);
    // Only the single highest-confidence verified row may show PRIMARY TARGET.
    const showPrimaryBadge =
      !isMentionCard &&
      Boolean(primaryTargetKey) &&
      (res.url || res.ui_bucket || '') === primaryTargetKey;
    const mentionCategory = isMentionCard ? classifyMentionCategory(res) : null;

    const contactEmails = [];
    const contactPhones = [];
    if (showPrimaryBadge) {
      contactEmails.push(...globalContacts.emails);
      contactPhones.push(...globalContacts.phones);
    }
    if (res?.found_email) contactEmails.push(String(res.found_email).trim());
    if (res?.found_phone) contactPhones.push(String(res.found_phone).trim());

    return (
      <div key={`${res.url || res.ui_bucket || 'row'}-${idx}`}>
        <EvidenceCard
          res={{
            ...res,
            is_mention: showPrimaryBadge ? false : isMentionCard || !!res.is_mention,
            is_primary_identity: showPrimaryBadge,
            snippet,
          }}
          title={name}
          icon="👤"
          showPrimaryBadge={showPrimaryBadge}
          mentionCategory={mentionCategory}
          showSnippet={isMentionCard}
          compactUrl={isMentionCard}
          contactEmails={contactEmails}
          contactPhones={contactPhones}
        />
      </div>
    );
  };

  const handleFilter = (id) => {
    if (typeof onFilterChange === 'function') {
      onFilterChange(id);
      return;
    }
  };

  return (
    <div className="touch-manipulation space-y-2.5 text-sm">
      <PersonFeedFilterBar
        activeFilter={activeFilter}
        onFilterChange={handleFilter}
        counts={filterCounts}
      />

      {activeFilter !== 'all' && filteredResults.length === 0 && displayResults.length > 0 ? (
        <p className="rounded-xl border border-amber-200/20 bg-amber-950/30 px-3 py-2 text-center text-[11px] font-semibold text-amber-100/90">
          No results in <span className="font-black uppercase">{activeFilter}</span>. Try All or another
          category.
        </p>
      ) : null}

      {complianceAlerts.map((res, idx) => (
        <ComplianceAlertCard
          key={`compliance-${res.entity_checked || res.title || idx}`}
          res={res}
          reactKey={`compliance-${idx}`}
        />
      ))}

      {waybackArchives.map((res, idx) => (
        <WaybackTimelineCard
          key={`wayback-${res.target_url || res.url || idx}`}
          data={res}
          idx={`wayback-${idx}`}
        />
      ))}

      {verifiedResults.length > 0 ? (
        <section className="space-y-3" aria-label="Verified intelligence">
          <p className="px-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#00E5FF]/80">
            Verified Intelligence & Primary Profiles
          </p>
          <div className="space-y-3">
            {verifiedResults.map((res, idx) => renderCard(res, idx, {}))}
          </div>
        </section>
      ) : null}

      {mentionResults.length > 0 && activeFilter !== 'profiles' && activeFilter !== 'potential' ? (
        <>
          <div className="my-6 border-t border-slate-800/80" aria-hidden />
          <section
            className="overflow-hidden rounded-xl border border-white/10 bg-slate-900/40"
            aria-label="Public mentions and registry leads"
          >
            <button
              type="button"
              onClick={() => setMentionsOpen((o) => !o)}
              className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
              aria-expanded={mentionsOpen}
            >
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 sm:text-xs sm:tracking-[0.18em]">
                Public Mentions & Registry Leads
                <span className="ml-2 tabular-nums text-slate-500">({mentionResults.length})</span>
              </span>
              <span className="text-lg leading-none text-slate-500">{mentionsOpen ? '−' : '+'}</span>
            </button>
            {mentionsOpen ? (
              <div className="space-y-2.5 border-t border-white/10 px-2.5 pb-2.5 pt-2.5 sm:px-3 sm:pb-3 sm:pt-3">
                {mentionResults.map((res, idx) => renderCard(res, idx, { isMentionCard: true }))}
              </div>
            ) : null}
          </section>
        </>
      ) : null}

      {hiddenResults.length > 0 && (activeFilter === 'all' || activeFilter === 'potential') ? (
        <div className="mb-8 mt-4 overflow-hidden rounded-xl border border-white/10 bg-slate-800/50 backdrop-blur-md">
          <button
            type="button"
            onClick={() => setNoiseOpen((o) => !o)}
            className="flex w-full min-h-[44px] items-center justify-between gap-2 px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-slate-400 transition hover:bg-white/5"
            aria-expanded={noiseOpen || activeFilter === 'potential'}
            data-testid="person-secondary-leads-toggle"
          >
            <span className="sm:hidden">
              {`Low Confidence Leads (${hiddenResults.length}) ▾`}
            </span>
            <span className="hidden sm:inline">
              {`Show Secondary Matches & Low Confidence Leads (${hiddenResults.length})`}
            </span>
            <span className="hidden text-lg leading-none text-slate-400 sm:inline" aria-hidden>
              {noiseOpen || activeFilter === 'potential' ? '−' : '+'}
            </span>
          </button>
          {noiseOpen || activeFilter === 'potential' ? (
            <div className="space-y-3 border-t border-white/5 bg-slate-900/40 p-3">
              {hiddenResults.map((res, idx) => renderCard(res, idx, { isMentionCard: true }))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default PersonDiscovery;
